import React, { useEffect, useState } from 'react';
import { Link2, X } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/States';
import { useToast } from '../../components/ui/Toast';
import { useAuth, authHeaders } from '../../auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

export default function AssignmentsPage() {
  const { token } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState(null);
  const [plants, setPlants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState(false);

  const headers = { 'Content-Type': 'application/json', ...authHeaders(token) };

  const load = async () => {
    setError(false);
    try {
      const [usersRes, plantsRes, assignmentsRes] = await Promise.all([
        fetch(`${API_BASE}/admin/users`, { headers }),
        fetch(`${API_BASE}/plants`, { headers }),
        fetch(`${API_BASE}/admin/assignments`, { headers })
      ]);
      if (!usersRes.ok || !plantsRes.ok || !assignmentsRes.ok) throw new Error('load failed');
      const usersData = await usersRes.json();
      const plantsData = await plantsRes.json();
      const assignmentsData = await assignmentsRes.json();
      const normalized = Array.isArray(assignmentsData)
        ? assignmentsData
        : Object.entries(assignmentsData || {}).map(([userId, plantIds]) => ({ userId, plantIds: Array.isArray(plantIds) ? plantIds : [] }));
      setUsers(usersData);
      setPlants(plantsData);
      setAssignments(normalized);
    } catch {
      setUsers([]);
      setError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const assignPlant = async (userId, plantId) => {
    if (!plantId) return;
    try {
      const res = await fetch(`${API_BASE}/admin/assignments`, { method: 'POST', headers, body: JSON.stringify({ userId, plantId }) });
      if (!res.ok) throw new Error((await res.json()).error || "Erreur d'affectation");
      toast.success('Centrale affectée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const unassignPlant = async (userId, plantId) => {
    try {
      const res = await fetch(`${API_BASE}/admin/assignments/${userId}/${plantId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur de retrait');
      toast.success('Centrale retirée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const normalUsers = (users || []).filter(u => u.role === 'user');

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Affectations' }]} />
      <div className="page-header">
        <h1><Link2 size={22} /> Affectations de Centrales</h1>
      </div>

      {users === null ? (
        <LoadingState label="Chargement..." />
      ) : error ? (
        <ErrorState label="Impossible de charger les affectations" />
      ) : normalUsers.length === 0 ? (
        <EmptyState label="Aucun utilisateur non-admin" />
      ) : (
        <div className="section">
          {normalUsers.map(user => {
            const assigned = assignments.find(a => a.userId === user.id);
            const assignedPlantIds = Array.isArray(assigned?.plantIds) ? assigned.plantIds : [];
            const available = plants.filter(p => !assignedPlantIds.includes(p.plant_id));
            return (
              <div key={user.id} className="summary-card" style={{ marginBottom: 14 }}>
                <div className="summary-label">{user.name} ({user.email})</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '10px 0' }}>
                  {assignedPlantIds.length === 0 ? (
                    <span className="summary-sub">Aucune centrale affectée</span>
                  ) : assignedPlantIds.map(plantId => (
                    <span key={plantId} className="alert-item" style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {plants.find(p => p.plant_id === plantId)?.name || plantId}
                      <button className="icon-btn icon-btn-danger" style={{ padding: 2, border: 'none' }} onClick={() => unassignPlant(user.id, plantId)} title="Retirer">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  onChange={(e) => { assignPlant(user.id, e.target.value); e.target.value = ''; }}
                  defaultValue=""
                >
                  <option value="">Affecter une centrale...</option>
                  {available.map(p => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
